import {Turnstile, TurnstileInstance} from '@marsidev/react-turnstile';
import clsx from 'clsx';
import {RefObject, useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {getSitekey} from './getSitekey';
import styles from './TurnstileWidget.module.css';
import {fetchChannel} from '../api/rainbow';

const TURNSTILE_CHANNEL_FEATURE = 'turnstile_captcha_web';

export const TURNSTILE_FORM_FIELD = 'turnstileToken';

let turnstileRef: RefObject<TurnstileInstance | null> | null = null;

export const resetTurnstile = () => {
  turnstileRef?.current?.reset();
};

export const getIsTurnstileEnabled = async (hostname: string) => {
  if (!getSitekey(hostname)) {
    return false;
  }

  try {
    const {channelFeatures} = await fetchChannel();
    return channelFeatures.includes(TURNSTILE_CHANNEL_FEATURE);
  } catch {
    return false;
  }
};

export const TurnstileWidget = () => {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    getIsTurnstileEnabled(window.location.hostname).then(setIsEnabled);
  }, []);

  const siteKey = getSitekey(window.location.hostname);

  if (!isEnabled || !siteKey) {
    return null;
  }

  return <TurnstileElement siteKey={siteKey} />;
};

const TurnstileElement = ({siteKey}: {siteKey: string}) => {
  const ref = useRef<TurnstileInstance | null>(null);
  const {
    register,
    unregister,
    setValue,
    setError,
    clearErrors,
    formState: {errors},
  } = useFormContext();
  const isTurnstileError = Boolean(errors[TURNSTILE_FORM_FIELD]);

  useEffect(() => {
    turnstileRef = ref;
    register(TURNSTILE_FORM_FIELD, {
      required: 'Turnstile verification is required',
    });

    return () => {
      unregister(TURNSTILE_FORM_FIELD);
      turnstileRef = null;
    };
  }, [register, unregister]);

  const setTokenValue = (token: string) => {
    setValue(TURNSTILE_FORM_FIELD, token);
    clearErrors(TURNSTILE_FORM_FIELD);
  };

  return (
    <div className={clsx({[styles.error]: isTurnstileError})}>
      <Turnstile
        id={TURNSTILE_FORM_FIELD}
        siteKey={siteKey}
        ref={ref}
        options={{
          theme: 'light',
          size: 'flexible',
        }}
        onSuccess={setTokenValue}
        onExpire={() => {
          setTokenValue('');
          ref.current?.reset();
        }}
        onError={() =>
          setError(TURNSTILE_FORM_FIELD, {
            type: 'manual',
            message: 'Turnstile verification failed',
          })
        }
      />
    </div>
  );
};
